import { postRequest, getRequest, putRequest, deleteRequest, postRequestWithHeaders } from "../api/apiService";

/**
 * Creates a new asset by sending a POST request to the server.
 *
 * @param {string|Object} assetDataOrName - The name of the asset OR an object containing all asset details.
 * @param {string} [description] - A brief description of the asset (if first arg is name).
 * @returns {Promise<Object>} - A promise that resolves to an object containing the success status and the response data or error.
 */
export const createAsset = async (assetDataOrName, description) => {
  let payload;
  if (typeof assetDataOrName === 'object' && assetDataOrName !== null) {
    payload = assetDataOrName;
  } else {
    payload = { name: assetDataOrName, description };
  }

  try {
    const response = await postRequest("/api/assets", payload);
    return { success: true, data: response.data }; // Return the actual data
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Fetches all assets from the server.
 */
export const fetchAllAssets = async () => {
  try {
    const response = await getRequest("/api/assets");
    return response.data; // Assuming the response is an array of assets
  } catch (error) {
    throw error;
  }
};



/**
 * Fetches details of a specific asset by its ID.
 * 
 * @param {string} id - The ID of the asset to fetch.
 * @returns {Promise<Object>} - A promise that resolves to the asset details.
 */
export const fetchAssetDetailsById = async (id) => {
  try {
    const response = await getRequest(`/api/assets/${id}`);
    return response; // Assuming the response is an array of assets
  } catch (error) {
    throw error;
  }
};


/**
 * Assigns a location to an asset by sending a PUT request.
 * 
 * This function sends a PUT request to the API to assign the specified location 
 * to the asset with the given assetId. It returns the response from the API call.
 * 
 * @param {string} assetId - The ID of the asset to which the location will be assigned.
 * @param {string} locationId - The ID of the location to assign to the asset.
 * @returns {Promise} A promise that resolves with the API response.
 * @throws {Error} If the API request fails, the error is thrown.
 */
export const assignLocationToAsset = async (assetId, locationId) => {
  try {
    const response = await putRequest(`/api/assets/${assetId}/assign-location/${locationId}`);
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Assigns a parent asset to an asset by sending a PUT request.
 * 
 * @param {string} assetId - The ID of the asset to which the parent asset will be assigned.
 * @param {string} parentId - The ID of the parent asset to assign.
 * @returns {Promise} A promise that resolves with the API response.
 */
export const assignParentAsset = async (assetId, parentId) => {
  try {
    const response = await putRequest(`/api/assets/${assetId}/assign-parent/${parentId}`);
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Assigns a spareOf asset to an asset by sending a PUT request.
 * 
 * @param {string} assetId - The ID of the asset to which the spareOf asset will be assigned.
 * @param {string} spareOfId - The ID of the spareOf asset to assign.
 * @returns {Promise} A promise that resolves with the API response.
 */
export const assignSpareOfAsset = async (assetId, spareOfId) => {
  try {
    const response = await putRequest(`/api/assets/${assetId}/assign-spareof/${spareOfId}`);
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Deletes an asset by its ID by making an HTTP DELETE request.
 * 
 * @param {string} assetId - The ID of the asset to be deleted.
 * @returns {Promise} - Resolves with the server's response if the request is successful.
 * @throws {Error} - Throws an error if the deletion request fails.
 */
export const deleteAssetById = async (assetId) => {
  if (!assetId || assetId === 'undefined') return Promise.reject(new Error("Invalid Asset ID"));
  try {
    const response = await deleteRequest(`/api/assets/${assetId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Uploads multiple image files to the server for a given asset.
 *
 * @param {Number} assetId - The ID of the asset to associate the images with.
 * @param {File[]} files - The list of image files to upload.
 * @returns {Object} - { success: Boolean, data: Object | error: Object }
 */
export const uploadAssetImage = async (assetId, files) => {
  const formData = new FormData();

  // Append multiple files
  files.forEach((file) => {
    formData.append("files", file);
  });

  try {
    const response = await postRequestWithHeaders(`/api/assets/upload/${assetId}`, formData, {});

    console.log("Response Data:", response); // Log the response to confirm

    // Directly check if response has `data` field
    if (response.status !== 200) {
      throw new Error(response.statusText || "Image upload failed");
    }

    return { success: true, data: response.data }; // Access response.data directly
  } catch (error) {
    console.error("Error while uploading asset image:", error);
    return { success: false, error };
  }
};


/**
 * Sets an image as the primary image for an asset.
 * @param {string} imageId - ID of the image to set as primary.
 */
export const setPrimaryAssetImage = async (imageId) => {
  try {
    const response = await putRequest(`/api/assets/images/${imageId}/primary`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Deletes an asset image by its ID.
 * @param {string} imageId - ID of the image to delete.
 */
export const deleteAssetImage = async (imageId) => {
  if (!imageId || imageId === 'undefined') return Promise.reject(new Error("Invalid Image ID"));
  try {
    const response = await deleteRequest(`/api/assets/images/${imageId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Updates an existing asset.
 * @param {string} assetId - The ID of the asset to update.
 * @param {Object} assetData - The updated asset data.
 * @returns {Promise<Object>} - A promise that resolves to the updated asset.
 */
export const updateAsset = async (assetId, assetData) => {
  try {
    const response = await putRequest(`/api/assets/${assetId}`, assetData);
    return response.data;
  } catch (error) {
    throw error;
  }
};
